import { Box, Button, Divider, IconChevronRight24, ResponsiveSection, Stack, Text, Toggle } from '@stitch-fix/mode-react';
import { Dispatch, SetStateAction, useState } from 'react';
import { Link, useClientAuth } from '@stitch-fix/sf-next';
import { useSelectEvent, useScreenViewEvent } from '@stitch-fix/event-reporter';
import { useRouter } from 'next/router';
import { GetClientEmailOptOutQuery } from '../GetClientEmailOptOut';
import { ClientEmailOptOutUpdateMutation, useClientEmailOptOutUpdateMutation } from '../ClientEmailOptOutUpdateMutation';
import styles from './PreferenceCenter.module.scss';
type EmailPreferencesProps = {
  data: GetClientEmailOptOutQuery | undefined;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  setShowSuccessToast: Dispatch<SetStateAction<boolean>>;
};
export const PreferenceCenter = ({
  data,
  setShowErrorToast,
  setShowSuccessToast
}: EmailPreferencesProps) => {
  const isPrimaryHouseholdMember = data?.client?.household === null || data?.client?.id === data?.client?.household?.primaryClientId;
  const primaryHouseholdMemberName = data?.client?.household?.members.find(member => member.id === data?.client?.household?.primaryClientId)?.firstName;
  const {
    authStatus
  } = useClientAuth();
  const loggedIn = authStatus === 'authenticated';
  const hasSecondaryHouseholdMembers = Boolean(data?.client?.household?.members.some(member => member.id !== data.client?.id));
  const showHouseholdEmailPreferencesLink = loggedIn && isPrimaryHouseholdMember && hasSecondaryHouseholdMembers;
  const notificationSettings = data?.client?.notificationSettings;
  const emailNotificationSettings = notificationSettings?.email;
  const emailHeader = emailNotificationSettings?.header;
  const emailsSectionTitle = emailHeader?.title?.text ?? 'Emails';
  const emailHeaderDescriptionText = emailHeader?.description?.text;
  const emailPromotionalPreference = emailNotificationSettings?.preferences.find(pref => pref.channel === 'email' && pref.type === 'promotional');
  const promotionalEmailTitle = emailPromotionalPreference?.title ?? 'Promotional emails';
  const promotionalEmailDescription = emailPromotionalPreference?.description ?? 'Get exclusive offers, news and styling tips in promotional emails.';
  const householdPromotionalEmailsTitle = emailNotificationSettings?.householdPromotionalEmailsTitle?.text || 'Manage household email preferences';
  const householdPromotionalEmailsDescription = emailNotificationSettings?.householdPromotionalEmailsDescription?.text;
  const smsNotificationSettings = notificationSettings?.sms;
  const smsSectionTitle = smsNotificationSettings?.header?.title?.text ?? 'SMS Notifications';
  const smsSignUpDescription = smsNotificationSettings?.header?.description?.text ?? 'Be the first to hear about sales, new arrivals, style tips and more.';
  const showSmsSignUpCta = Boolean(smsNotificationSettings?.smsSignUpButtonTitle?.text);
  const smsSignUpButtonLabel = smsNotificationSettings?.smsSignUpButtonTitle?.text ?? 'Sign up for SMS messages';
  const smsHeaderDescriptionText = smsNotificationSettings?.header?.description?.text ?? '';
  const secondaryProfileDescription = notificationSettings?.secondaryProfileTextDescription?.text;
  const optOut = data?.client?.notificationPreference?.emailOptOut || false;
  const [emailPreference, setEmailPreference] = useState(!optOut);
  const router = useRouter();
  const emailFromParams = router.query.email !== undefined ? router.query.email.toString() : null;

  // Legacy before sf_client_external_id
  const externalIdFromParams = router.query.external_id !== undefined ? router.query.external_id.toString() : null;
  const sfClientExternalIdFromParams = router.query.sf_client_external_id !== undefined ? router.query.sf_client_external_id.toString() : null;
  const tokenFromParams = router.query.bsft_auth !== undefined ? router.query.bsft_auth.toString() : null;
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'screen_view',
    name: 'notifications_settings',
    locale: 'en-US',
    region: 'US',
    source_app: 'growth-next-ui',
    content_id: isPrimaryHouseholdMember ? 'primary_client' : 'secondary_client'
  });
  const {
    reportSelectEvent
  } = useSelectEvent();
  const handleToggleChange = (value: boolean) => {
    setEmailPreference(value);
    reportSelectEvent({
      schema: 'select',
      name: 'email_toggle',
      action_name: value ? 'email_opt_in_promotional' : 'email_opt_out_promotional'
    });
    updateClientEmailOptOutMutation({
      variables: {
        input: {
          optOut: !value,
          token: tokenFromParams,
          email: emailFromParams,
          externalId: externalIdFromParams || sfClientExternalIdFromParams
        }
      }
    }).catch(() => undefined);
  };
  const handleSmsClick = () => {
    reportSelectEvent({
      schema: 'select',
      name: 'sms_button',
      action_name: 'sms_open_page'
    });
  };
  const smsSubscribedPrimaryText = smsHeaderDescriptionText;
  const [updateClientEmailOptOutMutation, {
    loading: loadingClientEmailOptOutMutation
  }] = useClientEmailOptOutUpdateMutation({
    errorPolicy: 'all',
    onError: () => {
      setShowErrorToast(true);
    },
    onCompleted: ({
      clientNotificationEmailOptOutUpdate
    }: ClientEmailOptOutUpdateMutation) => {
      if (!clientNotificationEmailOptOutUpdate?.result) {
        setShowErrorToast(true);
        return;
      }
      const {
        result
      } = clientNotificationEmailOptOutUpdate;
      if ('emailOptOut' in result) {
        setShowSuccessToast(true);
      } else {
        setShowErrorToast(true);
      }
    }
  });
  const emailOptInToggle = () => {
    return <Toggle name="email-promotions-toggle" isSelected={emailPreference} onChange={value => {
      handleToggleChange(value);
    }} disabled={loadingClientEmailOptOutMutation} container={toggleData => <Stack spacing={0.5}>
            <Box className={styles.flexBetweenRow}>
              <Box className={styles.flexGrowText}>
                <Text setting="title-xxsmall" family="regular" scale="3" height="standard">
                  {promotionalEmailTitle}
                </Text>
              </Box>
              {toggleData.toggleUi}
            </Box>
            <Text scale="2" family="regular" height="standard" color="gray-35">
              {promotionalEmailDescription}
            </Text>
          </Stack>} />;
  };
  return <EventProvider>
      <ResponsiveSection py={1.5}>
        <Stack spacing={1.5}>
          <Text setting="title-medium">{emailsSectionTitle}</Text>
          {loggedIn && <Text setting="body-small">{emailHeaderDescriptionText}</Text>}
          {emailOptInToggle()}
          {showHouseholdEmailPreferencesLink && <Stack spacing={1.5}>
              <Link size="cta" variant="inherit" href="/settings/notifications/householdEmail">
                <Box className={styles.fullWidth}>
                  <Stack spacing={0.5}>
                    <Box className={styles.flexBetweenRow}>
                      <Box className={styles.flexGrowText}>
                        <Text setting="title-xxsmall" family="regular" scale="3" height="standard">
                          {householdPromotionalEmailsTitle}
                        </Text>
                      </Box>
                      <Box className={styles.chevronWrap}>
                        <IconChevronRight24 purpose="decorative" />
                      </Box>
                    </Box>
                    {householdPromotionalEmailsDescription ? <Text scale="2" family="regular" height="standard" color="gray-35">
                        {householdPromotionalEmailsDescription}
                      </Text> : null}
                  </Stack>
                </Box>
              </Link>
            </Stack>}
          <Divider />
          <Text setting="title-medium">{smsSectionTitle}</Text>
          {isPrimaryHouseholdMember ? <Stack spacing={1.5}>
              {showSmsSignUpCta ? <Box maxWidth="30rem">
                  <Stack spacing={2}>
                    <Text setting="body-small">{smsSignUpDescription}</Text>
                    <Button width="fill" variant="outline" as="a" href="/settings/sms" onClick={handleSmsClick}>
                      {smsSignUpButtonLabel}
                    </Button>
                  </Stack>
                </Box> : <Text setting="body-small">{smsSubscribedPrimaryText}</Text>}
            </Stack> : <Text setting="body-small">
              {secondaryProfileDescription ?? `All other notifications are managed on ${primaryHouseholdMemberName || 'your primary'}’s account.`}
            </Text>}
        </Stack>
      </ResponsiveSection>
    </EventProvider>;
};